import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import api from '@/lib/api';
import { CustomSelect } from '@/components/CustomSelect';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, User, Key, Database, Shield, Plus, Eye, EyeOff } from 'lucide-react';

const inputCls = "w-full bg-white border border-[#E2DDD3] rounded-xl px-4 py-3 text-[14px] text-[#1C1C1C] font-sans placeholder:text-[#D9D3C8] focus:border-[#1F3A34] focus:ring-1 focus:ring-[#1F3A34]/20 focus:outline-none transition-all";
const labelCls = "text-[12px] text-[#5A5A5A] font-sans font-medium mb-1.5 block";

export default function Settings() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  // Add User form
  const [showAddUser, setShowAddUser] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('Analyst');
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    // Load mock users + current user
    setUsers([
      { name: user?.name || 'You', email: user?.email || '', role: 'Owner', status: 'Active' },
    ]);
  }, [user]);

  const handleAddUser = async () => {
    setAddError(''); setAddSuccess(''); setSaving(true);
    try {
      const { data } = await api.post('/auth/register', { name: newName, email: newEmail, password: newPassword });
      setUsers(prev => [...prev, { name: newName, email: newEmail, role: newRole, status: 'Active' }]);
      setAddSuccess(`User ${newName} (${newEmail}) created successfully.`);
      setNewName(''); setNewEmail(''); setNewPassword(''); setShowAddUser(false);
    } catch (e) {
      setAddError(e.response?.data?.detail || 'Failed to create user.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      <div>
        <p className="text-[12px] text-[#8C877F] font-sans font-medium mb-1">Settings</p>
        <h1 className="font-serif text-[36px] text-[#1C1C1C] font-medium tracking-tight">Configuration</h1>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-[#ECE7DE] border border-[#E2DDD3] rounded-xl p-1 h-auto flex-wrap">
          {[
            { v: 'profile', l: 'Profile', i: User },
            { v: 'users', l: 'Users & Roles', i: Shield },
            { v: 'api', l: 'API Connections', i: Key },
            { v: 'data', l: 'Data Quality', i: Database },
          ].map(t => (
            <TabsTrigger key={t.v} value={t.v} className="text-[12px] font-sans rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1F3A34] data-[state=active]:shadow-sm px-4 py-2 flex items-center gap-1.5">
              <t.i className="w-3.5 h-3.5" /> {t.l}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Profile */}
        <TabsContent value="profile" className="mt-6 space-y-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Organization</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Company Name</label><input className={inputCls} defaultValue="EstaFlow" /></div>
              <div><label className={labelCls}>Currency</label>
                <CustomSelect value="EUR" onChange={() => {}} options={[{value:'EUR',label:'EUR — Euro'},{value:'USD',label:'USD — Dollar'},{value:'GBP',label:'GBP — Pound'}]} />
              </div>
              <div><label className={labelCls}>Timezone</label>
                <CustomSelect value="eu-ams" onChange={() => {}} options={[{value:'eu-ams',label:'Europe/Amsterdam'},{value:'eu-lon',label:'Europe/London'}]} />
              </div>
              <div><label className={labelCls}>Locale</label>
                <CustomSelect value="nl" onChange={() => {}} options={[{value:'nl',label:'nl-NL (Nederlands)'},{value:'en',label:'en-US (English)'}]} />
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Your Profile</p>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-[#E6EFEA] flex items-center justify-center text-lg font-serif font-semibold text-[#1F3A34]">{user?.name?.[0]}</div>
              <div>
                <p className="text-[16px] text-[#1C1C1C] font-sans font-semibold">{user?.name}</p>
                <p className="text-[13px] text-[#8C877F]">{user?.email}</p>
                <Badge className="mt-1.5 rounded-lg bg-[#E6EFEA] text-[#1F3A34] border-[#1F3A34]/15 text-[10px] font-sans font-medium">{user?.plan || 'trial'}</Badge>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Users & Roles */}
        <TabsContent value="users" className="mt-6 space-y-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <div className="flex items-center justify-between">
              <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Team Members</p>
              <button onClick={() => setShowAddUser(!showAddUser)}
                className="flex items-center gap-1.5 px-4 py-2 bg-[#1F3A34] text-white rounded-lg text-[13px] font-sans font-medium hover:bg-[#285047] transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add User
              </button>
            </div>
            <div className="space-y-1">
              {users.map((u, i) => (
                <div key={i} className="flex items-center justify-between py-3.5 border-b border-[#F4F1EB] last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#E6EFEA] flex items-center justify-center text-[12px] font-sans font-semibold text-[#1F3A34]">{u.name[0]}</div>
                    <div>
                      <p className="text-[14px] text-[#1C1C1C] font-sans font-medium">{u.name}</p>
                      <p className="text-[12px] text-[#8C877F]">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="rounded-lg text-[11px] bg-[#F4F1EB] text-[#5A5A5A] border-[#E2DDD3] font-sans font-medium">{u.role}</Badge>
                    <span className="text-[11px] text-[#2E6F57] font-sans font-medium">{u.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Add User Form */}
          {showAddUser && (
            <div className="bg-white border border-[#1F3A34]/20 rounded-xl p-6 space-y-4 animate-fade-in" style={{ boxShadow: '0 4px 16px rgba(31,58,52,0.08)' }}>
              <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Create New User</p>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>Full Name *</label><input className={inputCls} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jan Bakker" /></div>
                <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="jan@company.com" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className={labelCls}>Password *</label><input type="password" className={inputCls} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 characters" /></div>
                <div><label className={labelCls}>Role</label>
                  <CustomSelect value={newRole} onChange={setNewRole} options={[
                    {value:'Owner',label:'Owner',sub:'Full access'},{value:'Admin',label:'Admin',sub:'Manage users & data'},
                    {value:'Analyst',label:'Analyst',sub:'View, edit, export'},{value:'Viewer',label:'Viewer',sub:'Read-only access'}
                  ]} testId="select-role" />
                </div>
              </div>
              {addError && <p className="text-[13px] text-[#8A3C3C] font-sans">{addError}</p>}
              {addSuccess && <p className="text-[13px] text-[#2E6F57] font-sans">{addSuccess}</p>}
              <div className="flex gap-3">
                <button onClick={handleAddUser} disabled={!newName || !newEmail || !newPassword || saving}
                  className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[13px] px-6 py-2.5 font-sans font-medium transition-colors disabled:opacity-40">
                  {saving ? 'Creating...' : 'Create User'}
                </button>
                <button onClick={() => { setShowAddUser(false); setAddError(''); }}
                  className="border border-[#E2DDD3] text-[#5A5A5A] hover:border-[#1F3A34]/30 rounded-lg text-[13px] px-6 py-2.5 font-sans font-medium transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Permissions */}
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold mb-4">Permission Matrix</p>
            <div className="overflow-x-auto">
              <table className="w-full text-[12px] font-sans">
                <thead><tr className="text-[#8C877F]"><th className="text-left py-2.5 font-medium">Permission</th><th className="text-center font-medium">Owner</th><th className="text-center font-medium">Admin</th><th className="text-center font-medium">Analyst</th><th className="text-center font-medium">Viewer</th></tr></thead>
                <tbody>
                  {[['View portfolio', true, true, true, true], ['Edit assets', true, true, true, false], ['Run AI analysis', true, true, true, false], ['Export reports', true, true, true, false], ['Manage users', true, true, false, false], ['Billing & plans', true, false, false, false]].map(([perm, ...vals], i) => (
                    <tr key={i} className="border-t border-[#F4F1EB]"><td className="py-2.5 text-[#1C1C1C]">{perm}</td>
                      {vals.map((v, j) => <td key={j} className="text-center">{v ? <Check className="w-4 h-4 text-[#2E6F57] mx-auto" /> : <span className="text-[#D9D3C8]">—</span>}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* API */}
        <TabsContent value="api" className="mt-6 space-y-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">API Key Configuration</p>
            <p className="text-[13px] text-[#8C877F]">Connect your property management or data provider API to import asset data automatically.</p>
            <div><label className={labelCls}>API Key</label>
              <div className="relative">
                <input className={inputCls + ' pr-10 font-sans'} type={showKey ? 'text' : 'password'} value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="sk_live_..." />
                <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C877F]">
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { if (!apiKey) { import('sonner').then(({ toast }) => toast.error('Enter an API key first')); return; } import('sonner').then(({ toast }) => toast.success('Connection successful')); }} className="bg-[#1F3A34] text-white hover:bg-[#285047] rounded-lg text-[13px] px-5 py-2.5 font-sans font-medium">Test Connection</button>
              <button onClick={() => { import('sonner').then(({ toast }) => toast.success('API settings saved')); }} className="border border-[#E2DDD3] text-[#5A5A5A] hover:border-[#1F3A34]/30 rounded-lg text-[13px] px-5 py-2.5 font-sans font-medium">Save</button>
            </div>
          </div>
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-4" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Data Mapping</p>
            <p className="text-[13px] text-[#8C877F]">Map API response fields to EstaFlow schema.</p>
            <div className="grid grid-cols-2 gap-3">
              {['Market Value', 'Annual Rent', 'Loan Amount', 'Interest Rate', 'Vacancy %', 'Operating Costs'].map(f => (
                <div key={f} className="flex items-center gap-3">
                  <span className="text-[12px] text-[#5A5A5A] w-28 font-sans font-medium">{f}</span>
                  <input className={inputCls + ' flex-1 text-[12px]'} placeholder={`api.${f.toLowerCase().replace(/\s/g, '_')}`} />
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Data Quality */}
        <TabsContent value="data" className="mt-6 space-y-6">
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-5" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Data Completeness</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { l: 'Assets with complete data', v: '7/7', pct: 100 },
                { l: 'Loans linked', v: '7/7', pct: 100 },
                { l: 'Market data synced', v: '0/7', pct: 0 },
              ].map(d => (
                <div key={d.l} className="space-y-2">
                  <p className="text-[12px] text-[#8C877F] font-sans font-medium">{d.l}</p>
                  <p className="text-[20px] font-sans font-semibold text-[#1C1C1C]">{d.v}</p>
                  <div className="w-full h-2 bg-[#F4F1EB] rounded-full"><div className="h-full rounded-full transition-all" style={{ width: `${d.pct}%`, backgroundColor: d.pct === 100 ? '#2E6F57' : '#A67C52' }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[#E2DDD3] rounded-xl p-6 space-y-3" style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <p className="text-[14px] text-[#1C1C1C] font-sans font-semibold">Validation Rules</p>
            {['IRR requires purchase price + annual rent + loan data', 'DSCR requires NOI + annual debt service', 'LTV requires market value + loan amount'].map((r, i) => (
              <div key={i} className="flex items-center gap-2.5 py-2">
                <Check className="w-4 h-4 text-[#2E6F57]" /><span className="text-[13px] text-[#1C1C1C] font-sans">{r}</span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
